


































































































import Vue from 'vue';
import axios from 'axios';

interface SupportingDocument {
  Title: string;
  S3Name: string;
  Access: string;
  CountryName: string;
  Uploaded: string;
  IsPrivate: boolean;
}

export default Vue.extend({
  name: 'AcidDocuments',

  data() {
    return {
      documents: [] as SupportingDocument[],
      loading: true,
      error: null as string | null,
      processingDocument: null as string | null,
      downloadingDocument: null as string | null
    };
  },

  computed: {
    publicDocuments(): SupportingDocument[] {
      return this.documents.filter(doc => !doc.IsPrivate);
    },

    privateDocuments(): SupportingDocument[] {
      return this.documents.filter(doc => doc.IsPrivate);
    },

    canViewPrivateDocuments(): boolean {
      const user = this.$store.getters.user;
      return this.isAdmin || (user && user.IsACID);
    },

    accessiblePrivateDocuments(): SupportingDocument[] {
      return this.canViewPrivateDocuments ? this.privateDocuments : [];
    },

    isAdmin(): boolean {
      return this.$store.getters['isAdmin'];
    }
  },

  async mounted(): Promise<void> {
    await this.loadDocuments();
  },

  methods: {
    async loadDocuments(): Promise<void> {
      try {
        this.loading = true;
        this.error = null;

        const response = await axios.post('/api/acid/documents');
        this.documents = response.data;
      } catch (error) {
        console.error('Error loading ACID documents:', error);
        this.error = 'Failed to load ACID documents. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async viewPrivateDocument(doc: SupportingDocument): Promise<void> {
      if (!this.canViewPrivateDocuments) {
        this.$router.push({ name: 'restricted' });
        return;
      }

      if (this.processingDocument === doc.S3Name) {
        return;
      }

      try {
        this.processingDocument = doc.S3Name;
        const response = await axios.get(`/api/acid/view/${doc.S3Name}`);

        if (response.data && response.data.filename) {
          const pdfResponse = await axios.get(`/api/print/pdf?filename=${response.data.filename}`, {
            responseType: 'blob'
          });

          const blobUrl = URL.createObjectURL(pdfResponse.data);
          this.$router.push({ name: 'pdfViewer', query: { file: blobUrl, returnTo: 'acidDocuments' } });
        } else {
          this.error = 'Unable to prepare the document for viewing.';
        }
      } catch (error) {
        console.error('Error preparing private document:', error);
        const serverMessage = error.response && error.response.data && (error.response.data.message || error.response.data.Message);

        if (error.response && error.response.status === 403) {
          this.error = serverMessage || 'You do not have permission to view this document.';
        } else if (error.response && error.response.status === 400) {
          this.error = serverMessage || 'This document type is not supported for in-browser viewing.';
        } else {
          this.error = serverMessage || 'Failed to open the document. Please try again.';
        }
      } finally {
        this.processingDocument = null;
      }
    },

    formatDate(dateString: string): string {
      return new Date(dateString).toLocaleDateString();
    },

    async downloadDocument(doc: SupportingDocument): Promise<void> {
      if (this.downloadingDocument === doc.S3Name) {
        return;
      }

      try {
        this.error = null;
        this.downloadingDocument = doc.S3Name;

        const response = await axios.get(`/api/acid/download/${doc.S3Name}`, {
          responseType: 'blob'
        });

        const contentType = response.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const s3Extension = doc.S3Name && doc.S3Name.includes('.')
          ? doc.S3Name.substring(doc.S3Name.lastIndexOf('.'))
          : '';
        const trimmedTitle = (doc.Title || '').trim();
        let filename = trimmedTitle;
        if (filename) {
          if (s3Extension && !filename.toLowerCase().endsWith(s3Extension.toLowerCase())) {
            filename += s3Extension;
          }
        } else {
          filename = doc.S3Name;
        }

        link.setAttribute('download', filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading document:', error);
        const serverMessage = error.response && error.response.data && (error.response.data.message || error.response.data.Message);
        this.error = serverMessage || 'Failed to download document. Please try again.';
      } finally {
        this.downloadingDocument = null;
      }
    }
  }
});
